/**
 * Design tokens for the marketing landing page.
 *
 * The landing page is art-directed independently of the in-app theme: it only
 * borrows light/dark polarity, then derives its own warm ink/paper ramp so the
 * page reads as one composed piece across all ten app themes.
 *
 * Type is fluid rather than breakpoint-stepped, and tracking is expressed as a
 * ratio of font size — large display sizes need far tighter tracking than body
 * copy, and a fixed pixel value can only be correct at one size.
 */
import { Platform } from 'react-native';

export const isWeb = Platform.OS === 'web';

/**
 * Single accent for the whole page: phthalo green. Deep and saturated, it
 * reads as editorial rather than "AI startup blue" — but it is dark enough
 * that it vanishes on a dark surface, so text and icons use `ACCENT_TINT`
 * there and fills step up to `ACCENT_BRIGHT`. Prefer `c.accent` /
 * `c.accentFill` from `landingPalette`, which resolve this per theme.
 */
export const ACCENT = '#123524';
export const ACCENT_BRIGHT = '#1F6B47';
export const ACCENT_TINT = '#5FB68F';
export const ON_ACCENT = '#FFFFFF';

/** Use the user-selected app accent when present; otherwise keep the marketing accent. */
export function resolveUserAccent(accentId, accentColorPrimary) {
  if (accentId && accentId !== 'multicolor') return accentColorPrimary || ACCENT;
  return ACCENT;
}

const ACCENT_RGB = '18,53,36';
const ACCENT_TINT_RGB = '95,182,143';

/** Translucent accent for washes, rings, and glows. `dark` picks the tint that survives a dark surface. */
export function accentAlpha(alpha, dark = false) {
  return `rgba(${dark ? ACCENT_TINT_RGB : ACCENT_RGB},${alpha})`;
}

/**
 * Full-bleed bands are the page's structural accent: a recessed surface that
 * separates one movement from the next. They follow the theme rather than
 * always being dark, so a light page never drops into an unexplained black box.
 * The tint is warm and clearly offset from the page colour so the band still
 * reads as a distinct panel.
 */
export function landingBand(isDark) {
  if (isDark) {
    return {
      bg: '#090C0A',
      ink: '#F3F6F3',
      inkSoft: 'rgba(243,246,243,0.78)',
      muted: 'rgba(243,246,243,0.56)',
      faint: 'rgba(243,246,243,0.40)',
      rule: 'rgba(243,246,243,0.12)',
      surface: 'rgba(247,245,241,0.05)',
      surfaceSoft: 'rgba(247,245,241,0.04)',
    };
  }
  return {
    bg: '#F1F3F0',
    ink: '#0F1512',
    inkSoft: 'rgba(15,21,18,0.74)',
    muted: 'rgba(15,21,18,0.56)',
    faint: 'rgba(15,21,18,0.38)',
    rule: 'rgba(15,21,18,0.11)',
    surface: '#FFFFFF',
    surfaceSoft: 'rgba(255,255,255,0.66)',
  };
}

/**
 * `ui-serif` resolves to New York on Apple platforms, which is a far better
 * editorial face than Georgia. Georgia stays last as the universal fallback.
 */
export const SERIF = "ui-serif, 'New York', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif";
export const MONO = "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace";

export const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
export const EASE_OUT = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

/** Viewport range across which fluid type and spacing interpolate. */
const FLUID_MIN_WIDTH = 390;
const FLUID_MAX_WIDTH = 1440;

/** Linearly interpolate a value between two viewport widths, clamped at both ends. */
export function fluid(width, min, max, minWidth = FLUID_MIN_WIDTH, maxWidth = FLUID_MAX_WIDTH) {
  if (!Number.isFinite(width) || width <= minWidth) return min;
  if (width >= maxWidth) return max;
  const t = (width - minWidth) / (maxWidth - minWidth);
  return Math.round((min + (max - min) * t) * 2) / 2;
}

/**
 * Build a display type style. `leading` is a multiple of font size and
 * `tracking` is an em ratio, so the optical result holds at every size.
 */
export function displayType(width, { min, max, leading = 0.98, tracking = -0.04 }) {
  const fontSize = fluid(width, min, max);
  return {
    fontSize,
    lineHeight: Math.round(fontSize * leading),
    letterSpacing: Math.round(fontSize * tracking * 10) / 10,
  };
}

/** Body copy scale: looser leading, near-neutral tracking. */
export function proseType(width, { min, max, leading = 1.5, tracking = -0.011 }) {
  const fontSize = fluid(width, min, max);
  return {
    fontSize,
    lineHeight: Math.round(fontSize * leading),
    letterSpacing: Math.round(fontSize * tracking * 10) / 10,
  };
}

/** Small all-caps labels. Positive tracking is what makes them read as labels. */
export function labelType(size = 11, tracking = 0.16) {
  return {
    fontSize: size,
    lineHeight: Math.round(size * 1.4),
    letterSpacing: Math.round(size * tracking * 10) / 10,
    textTransform: 'uppercase',
  };
}

/**
 * Ink/paper ramp. Neutrals are biased a touch cool (hue toward green) so they
 * feel related to the phthalo accent instead of fighting it.
 *
 * `accent` is safe for text and icons on the page surface; `accentFill` is
 * for solid buttons and progress bars. Both step lighter in dark mode.
 */
export function landingPalette(isDark) {
  if (isDark) {
    return {
      isDark: true,
      page: '#101310',
      ink: '#F3F6F3',
      inkSoft: 'rgba(243,246,243,0.78)',
      muted: 'rgba(243,246,243,0.56)',
      faint: 'rgba(243,246,243,0.34)',
      rule: 'rgba(243,246,243,0.10)',
      ruleStrong: 'rgba(243,246,243,0.22)',
      panel: 'rgba(243,246,243,0.035)',
      card: 'rgba(243,246,243,0.045)',
      cardSolid: '#111412',
      accent: ACCENT_TINT,
      accentFill: ACCENT_BRIGHT,
      accentWash: accentAlpha(0.14, true),
      accentSoft: accentAlpha(0.20, true),
      accentRing: accentAlpha(0.42, true),
      // Shadows are for separation, not levitation: one tight contact line
      // plus a short, low-alpha ambient. Borders do most of the work.
      shadow: '0 1px 0 rgba(0,0,0,0.5), 0 12px 32px -8px rgba(0,0,0,0.5)',
      shadowSoft: '0 1px 0 rgba(0,0,0,0.45), 0 6px 18px -6px rgba(0,0,0,0.4)',
    };
  }
  return {
    isDark: false,
    page: '#FAFAF7',
    ink: '#0F1512',
    inkSoft: 'rgba(15,21,18,0.76)',
    muted: 'rgba(15,21,18,0.56)',
    faint: 'rgba(15,21,18,0.34)',
    rule: 'rgba(15,21,18,0.10)',
    ruleStrong: 'rgba(15,21,18,0.20)',
    panel: 'rgba(15,21,18,0.022)',
    card: '#FFFFFF',
    cardSolid: '#FFFFFF',
    accent: ACCENT,
    accentFill: ACCENT,
    accentWash: accentAlpha(0.08),
    accentSoft: accentAlpha(0.12),
    accentRing: accentAlpha(0.38),
    shadow: '0 1px 2px rgba(15,21,18,0.06), 0 12px 32px -8px rgba(15,21,18,0.10)',
    shadowSoft: '0 1px 2px rgba(15,21,18,0.05), 0 6px 18px -6px rgba(15,21,18,0.08)',
  };
}

/** Content measure and gutters. `wide` is for full-bleed bands, `text` caps line length. */
export const LAYOUT = {
  max: 1280,
  wide: 1440,
  text: 620,
};

export function sidePadding(width) {
  return fluid(width, 20, 64);
}

/**
 * Vertical rhythm between major sections. Sections pad top and bottom, so the
 * gap between two of them is twice this value.
 */
export function sectionSpace(width) {
  return fluid(width, 66, 104);
}

export function prefersReducedMotion() {
  if (!isWeb || typeof window === 'undefined') return false;
  return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
}
